import { useState } from 'react'
import { AlertCircle, Plus } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { RestaurantTable } from '../components/restaurant-table'
import { RestaurantForm } from '../components/restaurant-form'
import type { Restaurant } from '@/api/types'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { useRestaurants } from '@/features/products/hooks/use-restaurants'
import { PermissionGate } from '@/shared/components/permission-gate'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { ACTIONS } from '@/shared/constants/actions'

export function RestaurantsPage() {
  const { t: tRestaurants } = useTranslation('restaurants')
  const [formOpen, setFormOpen] = useState(false)
  const [edit, setEdit] = useState<Restaurant | null>(null)
  const { can } = usePermissions()
  const canManage = can(ACTIONS.RESTAURANT_MANAGE)
  const { data, isLoading, error } = useRestaurants()
  const restaurants = data?.items || []

  return (
    <PermissionGate action={ACTIONS.RESTAURANT_VIEW}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">{tRestaurants('title')}</h1>
            <p className="text-muted-foreground mt-1">
              {tRestaurants('description')}
            </p>
          </div>
          {canManage && (
            <Button
              onClick={() => {
                setEdit(null)
                setFormOpen(true)
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              {tRestaurants('createNew')}
            </Button>
          )}
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{tRestaurants('error.title')}</AlertTitle>
            <AlertDescription>
              {tRestaurants('error.description')}
            </AlertDescription>
          </Alert>
        )}

        <RestaurantTable
          restaurants={restaurants}
          isLoading={isLoading}
          onEdit={(r) => {
            setEdit(r)
            setFormOpen(true)
          }}
        />

        <RestaurantForm
          open={formOpen}
          onOpenChange={setFormOpen}
          initial={edit ?? undefined}
        />
      </div>
    </PermissionGate>
  )
}
